<template>
    <div class="py-xl flex h-full w-full items-center justify-center overflow-auto">
        <div
            class="gap-xl p-xl bg-lightest flex flex-col items-center rounded-sm"
            :class="{ 'self-start': onboarding }"
            style="width: 528px"
        >
            <section v-if="onboarding" class="gap-lg flex flex-col">
                <OnboardingForm />
            </section>
            <!-- Login Form Section with Fade Transition -->
            <transition v-else name="fade" mode="out-in">
                <section v-if="!showResetPass" class="gap-lg flex w-full flex-col">
                    <div class="flex justify-center">
                        <i class="i-logo h-5 w-24" />
                    </div>

                    <div class="gap-md flex flex-col">
                        <div class="gap-xs flex flex-col items-center">
                            <h2>Welcome to atNorth Service Portal</h2>
                            <p>To log in, please add some information</p>
                        </div>

                        <form class="gap-sm flex w-full flex-col" @submit.prevent="handleSubmit">
                            <div class="input-wrapper">
                                <input v-model="email" type="text" placeholder="Email" autocomplete="email" />
                            </div>

                            <div v-if="showPasswordStep" class="input-wrapper">
                                <input v-model="password" type="password" placeholder="Password" />
                            </div>

                            <p v-if="errorMessage" class="text-error text-xs">
                                {{ errorMessage }}
                            </p>

                            <div v-if="showPasswordStep" class="gap-sm flex justify-center">
                                <div class="gap-xs flex items-center justify-center"></div>
                                <p class="btn-link px-sm py-xs rounded-sm" @click="openResetPass">
                                    Forgot password?
                                </p>
                            </div>

                            <Button type="submit" class="w-full">
                                <i v-if="loggingIn" class="i-spinner-white animate-spin" />
                                <span v-else>{{ showPasswordStep ? 'Sign in' : 'Continue' }}</span>
                            </Button>

                            <button
                                v-if="showOptionalSsoBypassLink"
                                type="button"
                                class="mx-auto text-xs text-(--text-muted) underline hover:text-primary"
                                @click="continueWithoutSso"
                            >
                                Log in without SSO
                            </button>
                        </form>
                    </div>
                </section>
                <section v-else>
                    <resetPass @back="toggleResetPass" @reset="handlePassReset" />
                </section>
            </transition>
        </div>
    </div>
</template>

<script setup>
import { Button } from '@/components/ui/button';
import { useAuthStore } from '@/stores/auth';
import { computed, onMounted, ref, watch } from 'vue';
import resetPass from '@/components/login/reset-pass.vue';
import OnboardingForm from '@/components/onboarding-form.vue';
import { useRoute, useRouter } from 'vue-router';
import { getEnv } from '@/config/projectEnvVariables';

const authStore = useAuthStore();
const router = useRouter();
const route = useRoute();
const email = ref('');
const password = ref('');
const errorMessage = ref('');
const showResetPass = ref(false);

function openResetPass() {
    showResetPass.value = true;
}

function toggleResetPass() {
    showResetPass.value = !showResetPass.value;
}
const showPasswordStep = ref(false);
const loggingIn = ref(false);
const onboarding = ref(false);
const ssoEmailDomains = ['@atnorth.com', '@partners.atnorth.com'];
const validSsoModes = ['disabled', 'optional', 'enforced'];
const ssoMode = resolveSsoMode();
const ssoStateStorageKey = 'auth.sso.state';

const showOptionalSsoBypassLink = computed(() => {
    if (ssoMode !== 'optional' || showPasswordStep.value) {
        return false;
    }

    const trimmedEmail = email.value.trim();
    return trimmedEmail !== '' && isSsoEmail(trimmedEmail);
});

function isSsoEmail(emailAddress) {
    const normalizedEmail = emailAddress.toLowerCase();
    return ssoEmailDomains.some((domain) => normalizedEmail.endsWith(domain));
}

function resolveSsoMode() {
    const rawConfiguredMode = getEnv().VITE_AUTH_SSO_MODE;
    const configuredMode = (rawConfiguredMode ?? 'enforced').toLowerCase();
    if (validSsoModes.includes(configuredMode)) {
        console.info('[Auth] VITE_AUTH_SSO_MODE loaded:', configuredMode);
        return configuredMode;
    }

    console.warn('[Auth] Invalid VITE_AUTH_SSO_MODE value:', rawConfiguredMode, 'falling back to enforced');
    return 'enforced';
}

function shouldUseSso(emailAddress) {
    return ssoMode !== 'disabled' && isSsoEmail(emailAddress);
}

async function handleSubmit() {
    if (!showPasswordStep.value) {
        await continueWithEmail();
        return;
    }

    await login();
}

async function continueWithEmail() {
    const trimmedEmail = email.value.trim();
    if (!trimmedEmail) {
        errorMessage.value = 'Please enter your email.';
        return;
    }

    email.value = trimmedEmail;
    errorMessage.value = '';

    if (shouldUseSso(trimmedEmail)) {
        await triggerSSO();
        return;
    }

    showPasswordStep.value = true;
    password.value = '';
}

function continueWithoutSso() {
    showPasswordStep.value = true;
    password.value = '';
    errorMessage.value = '';
}

function getLoginRedirectTarget() {
    const redirect = route.query.redirect;
    if (typeof redirect !== 'string' || !redirect.startsWith('/') || redirect.startsWith('//')) {
        return undefined;
    }

    return redirect;
}

function getLoginRedirectUri() {
    const redirect = getLoginRedirectTarget();
    const query = redirect ? { redirect } : undefined;
    return `${window.location.origin}${router.resolve({ name: 'login', query }).href}`;
}

function generateSsoState() {
    if (window.crypto?.randomUUID) {
        return window.crypto.randomUUID();
    }
    return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

async function clearSsoCallbackQueryParams() {
    const nextQuery = { ...route.query };
    delete nextQuery.code;
    delete nextQuery.state;
    delete nextQuery.session_state;
    delete nextQuery.error;
    delete nextQuery.error_description;
    await router.replace({ query: nextQuery });
}

async function handleSsoCallback() {
    const callbackError = typeof route.query.error === 'string' ? route.query.error : '';
    const callbackCode = typeof route.query.code === 'string' ? route.query.code : '';
    const callbackState = typeof route.query.state === 'string' ? route.query.state : '';
    if (callbackError) {
        errorMessage.value = 'SSO login failed. Please try again.';
        localStorage.removeItem(ssoStateStorageKey);
        await clearSsoCallbackQueryParams();
        return;
    }
    if (!callbackCode) {
        return;
    }

    const expectedState = localStorage.getItem(ssoStateStorageKey) ?? '';
    if (expectedState && expectedState !== callbackState) {
        errorMessage.value = 'SSO login validation failed. Please try again.';
        localStorage.removeItem(ssoStateStorageKey);
        await clearSsoCallbackQueryParams();
        return;
    }

    loggingIn.value = true;
    errorMessage.value = '';
    try {
        const possibleError = await authStore.ssoExchange(
            callbackCode,
            getLoginRedirectUri(),
            getLoginRedirectTarget(),
        );
        if (possibleError?.data?.code >= 400 || possibleError?.data?.success === false) {
            errorMessage.value = 'SSO login failed. Please try again.';
            await clearSsoCallbackQueryParams();
        }
    } catch {
        errorMessage.value = 'SSO login failed. Please try again.';
        await clearSsoCallbackQueryParams();
    } finally {
        localStorage.removeItem(ssoStateStorageKey);
        loggingIn.value = false;
    }
}

async function triggerSSO() {
    loggingIn.value = true;
    errorMessage.value = '';
    const trimmedEmail = email.value.trim();
    const state = generateSsoState();
    localStorage.setItem(ssoStateStorageKey, state);
    try {
        const response = await authStore.getSsoAuthorizationUrl(trimmedEmail, getLoginRedirectUri(), state);
        const authorizationUrl = response?.data?.data?.authorization_url;
        if (!authorizationUrl) {
            throw new Error('Missing authorization URL');
        }
        window.location.assign(authorizationUrl);
    } catch {
        localStorage.removeItem(ssoStateStorageKey);
        errorMessage.value = 'Unable to start SSO login. Please try again.';
        loggingIn.value = false;
    }
}

async function login() {
    const trimmedEmail = email.value.trim();
    if (ssoMode === 'enforced' && shouldUseSso(trimmedEmail)) {
        email.value = trimmedEmail;
        showPasswordStep.value = false;
        password.value = '';
        errorMessage.value = '';
        await triggerSSO();
        return;
    }

    if (trimmedEmail === '' || password.value === '') {
        errorMessage.value = 'Please enter both email and password.';
        return;
    }

    loggingIn.value = true;

    try {
        const possibleError = await authStore.login(trimmedEmail, password.value, getLoginRedirectTarget());
        if (possibleError?.data?.code >= 400) {
            errorMessage.value = 'Invalid email or password.';
        } else if (possibleError?.data?.success === false) {
            errorMessage.value = 'Unknown error occurred. Please try again.';
        } else {
            errorMessage.value = '';
        }
    } catch {
        errorMessage.value = 'Invalid email or password.';
    } finally {
        loggingIn.value = false;
    }
}

onMounted(async () => {
    if (authStore.isAuthenticated) {
        await router.replace(getLoginRedirectTarget() ?? '/');
        return;
    }
    await handleSsoCallback();
});

async function handlePassReset(email) {
    // Perform password reset logic here
    await authStore.resetPass(email);
}

watch(showResetPass, (val) => {
    if (!val) {
        errorMessage.value = '';
        email.value = '';
        password.value = '';
        showPasswordStep.value = false;
    }
});
</script>

<style scoped>
@reference '@/assets/styles/default.css';

/* Fade transition styles */
.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.5s;
}
.fade-enter, .fade-leave-to /* .fade-leave-active in <2.1.8 */ {
    opacity: 0;
}
</style>
